const deferredIdsByResponse = new WeakMap()
const deferredSlideIds = new Set()

const withZeroCounts = (options) => options.map((option) => (option.count === undefined
  ? { ...option, count: 0 }
  : option))

// general-api leaves multipleCountAnswer unset only when it honoured skipAnswerCounts; an older
// build that ignored the param still sends every count, and those slides are never deferred.
export const deferMissingAnswerCounts = (res) => {
  const slides = res && res.body && res.body.Slides
  if (!slides) return res
  const ids = slides.filter((slide) => slide.multipleCountAnswer === undefined).map((slide) => slide.id)
  if (!ids.length) return res
  const deferred = {
    ...res,
    body: {
      ...res.body,
      // Live answer handlers add to option.count, so an unset count would turn into NaN.
      Slides: slides.map((slide) => (slide.multipleCountAnswer === undefined && slide.SlideOptions
        ? { ...slide, SlideOptions: withZeroCounts(slide.SlideOptions) }
        : slide)),
    },
  }
  deferredIdsByResponse.set(deferred, ids)
  return deferred
}

// Runs on every read of the cached response, so a deck reopened from the cache defers again.
export const rememberDeferredAnswerCounts = (res) => {
  const ids = res && deferredIdsByResponse.get(res)
  if (ids) ids.forEach((id) => deferredSlideIds.add(id))
}

export const hasDeferredAnswerCounts = (slides) => (slides || []).some((slide) => deferredSlideIds.has(slide.id))

const BATCH_SIZE = 10
const batchDeniedDecks = new Set()

const countsPatch = (slide, fetched) => {
  const answerCount = fetched.multipleCountAnswer || 0
  const fetchedCounts = new Map((fetched.SlideOptions || []).map((option) => [option.id, option.count || 0]))
  const patch = { id: slide.id, multipleCountAnswer: answerCount, voteCache: answerCount }
  // The throttled vote-count update writes countBackup back as the total, so a stale or zero value would reset it.
  if (slide.countBackup !== undefined) patch.countBackup = answerCount
  if (slide.SlideOptions) {
    patch.SlideOptions = slide.SlideOptions.map((option) => (fetchedCounts.has(option.id)
      ? { ...option, count: fetchedCounts.get(option.id) }
      : option))
  }
  return patch
}

const findStoredSlide = (state, slideId) => {
  const { slideActive, presentation } = state
  if (slideActive && slideActive.id === slideId) return slideActive
  return ((presentation && presentation.slides) || []).find((slide) => slide.id === slideId)
}

// The batch endpoint 403s unless every id belongs to the open deck, so only that deck's slides join it.
const pickBatch = (state, slideId) => {
  if (batchDeniedDecks.has(state.presentation && state.presentation.id)) return [slideId]
  const slides = (state.presentation && state.presentation.slides) || []
  const start = slides.findIndex((slide) => slide.id === slideId)
  const following = slides.slice(start + 1)
    .filter((slide) => deferredSlideIds.has(slide.id))
    .map((slide) => slide.id)
  return start === -1 ? [slideId] : [slideId, ...following].slice(0, BATCH_SIZE)
}

const fetchOne = async (slideId, fetchSlide) => {
  const res = await fetchSlide(slideId)
  return [res && res.body && res.body.data].filter(Boolean)
}

// The batch route asks for edit rights, the single one only for view rights, so a viewer falls
// back to one slide per open for the rest of the session.
const fetchCounts = async (ids, { fetchSlides, fetchSlide, presentationId }) => {
  if (ids.length === 1) return fetchOne(ids[0], fetchSlide)
  try {
    const res = await fetchSlides(ids, presentationId)
    return (res && res.data) || []
  } catch (error) {
    if (!error || error.status !== 403) throw error
    batchDeniedDecks.add(presentationId)
    ids.slice(1).forEach((id) => deferredSlideIds.add(id))
    return fetchOne(ids[0], fetchSlide)
  }
}

const applyCounts = (store, fetched) => {
  const slide = findStoredSlide(store.state, fetched.id)
  if (!slide) return
  const patch = countsPatch(slide, fetched)
  store.commit('updateSlideById', patch)
  // Chart components seed tempAudienceCount from the slide on mount and write it back on
  // each live answer, so it must move with the loaded count or the next answer resets it.
  const { state } = store
  if (state.slideActive && state.slideActive.id === fetched.id) state.tempAudienceCount = patch.multipleCountAnswer
}

// Never rejects: it runs un-awaited on slide change, and a failed read leaves its slides deferred
// so the next open retries them.
export const loadDeferredSlideAnswerCounts = async (store, slideId, { fetchSlides, fetchSlide, onError }) => {
  if (!deferredSlideIds.has(slideId)) return
  const ids = pickBatch(store.state, slideId)
  ids.forEach((id) => deferredSlideIds.delete(id))
  const presentationId = store.state.presentation && store.state.presentation.id
  try {
    const fetched = await fetchCounts(ids, { fetchSlides, fetchSlide, presentationId })
    fetched.forEach((slide) => applyCounts(store, slide))
  } catch (error) {
    ids.forEach((id) => deferredSlideIds.add(id))
    onError(error)
  }
}
